import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/components/auth/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageContext";
import { useNavigate } from "react-router-dom";
import { feedbackQuery, feedbackRpc, feedbackStorage, triggerNotification } from "./supabaseHelper";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, X, Loader2, BellRing, LogIn, User } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import DuplicateHints from "./DuplicateHints";
import type { FeedbackType } from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: FeedbackType;
}

export default function FeedbackSubmitPanel({ open, onOpenChange, defaultType }: Props) {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const fb = t.feedback;

  const [type, setType] = useState<FeedbackType>(defaultType ?? "support");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [expected, setExpected] = useState("");
  const [actual, setActual] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Sync type when panel opens with a specific defaultType
  useEffect(() => {
    if (open && defaultType) setType(defaultType);
  }, [open, defaultType]);

  const reset = () => {
    setTitle("");
    setDescription("");
    setSteps("");
    setExpected("");
    setActual("");
    setContactEmail("");
    setFiles([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    const valid = selected.filter((f) => f.size <= 5 * 1024 * 1024 && f.type.startsWith("image/"));
    setFiles((prev) => [...prev, ...valid].slice(0, 3));
    if (fileRef.current) fileRef.current.value = "";
  };

  const isSupport = type === "support";

  const handleSubmit = async () => {
    console.log("🟡 Feedback submit triggered", { user: user?.id, type, title, description });
    if (!title.trim() || !description.trim()) {
      console.warn("🔴 Feedback submit aborted: missing title/description");
      return;
    }
    // Support without login requires contact email
    if (isSupport && !user && !contactEmail.trim()) {
      toast({ title: fb?.contactEmailRequired ?? "Please provide a contact email", variant: "destructive" });
      return;
    }
    setSubmitting(true);

    try {
      const payload: any = {
        type,
        title: title.trim(),
        description: description.trim(),
        steps_to_reproduce: type === "bug" ? steps.trim() || null : null,
        expected_behavior: type === "bug" ? expected.trim() || null : null,
        actual_behavior: type === "bug" ? actual.trim() || null : null,
      };

      if (user) {
        payload.user_id = user.id;
      }

      if (isSupport && !user) {
        payload.contact_email = contactEmail.trim();
      }

      console.log("🟡 Inserting feedback_items:", payload);
      const { data: item, error } = await feedbackQuery("feedback_items")
        .insert(payload)
        .select("id")
        .single();

      console.log("🟡 Feedback insert result:", { item, error });

      if (error) throw error;

      // Upload attachments (only for bugs with logged-in user)
      // Bucket is PRIVATE — store the storage path and resolve via signed URLs at read time.
      if (files.length && item && user) {
        for (const file of files) {
          const path = `${user.id}/${item.id}/${Date.now()}-${file.name}`;
          const { error: uploadErr } = await feedbackStorage
            .from("feedback-attachments")
            .upload(path, file);
          if (uploadErr) {
            console.error("🔴 Attachment upload failed:", uploadErr);
            continue;
          }
          await feedbackQuery("feedback_attachments").insert({
            feedback_item_id: item.id,
            user_id: user.id,
            storage_path: path,
            file_url: path, // legacy column kept populated for back-compat
            file_name: file.name,
            file_type: file.type,
          });
        }
      }

      // Auto-subscribe if email notifications requested (only for logged-in users)
      if (notifyEmail && item && user) {
        const { data: subResult } = await feedbackRpc("subscribe_feedback_item", { _feedback_item_id: item.id });
        if (subResult?.global_enabled) {
          toast({ title: fb?.globalEmailEnabled ?? "Email notifications have been enabled globally" });
          refreshProfile();
        }
      }

      // Trigger notification for admins (in-app + email + push)
      if (item?.id && user) {
        triggerNotification({
          feedbackItemId: item.id,
          notificationType: "new_item",
          actorUserId: user.id,
        });
      }

      const successMsg = isSupport
        ? (fb?.supportSubmitted ?? "Support request sent successfully!")
        : (fb?.submitted ?? "Feedback submitted!");
      toast({ title: successMsg });
      reset();
      onOpenChange(false);
    } catch (err: any) {
      console.error("Feedback submit error:", err);
      toast({ title: err.message || "Error", description: JSON.stringify(err), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:w-[420px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isSupport ? (fb?.supportTitle ?? "Contact Support") : (fb?.submitTitle ?? "Submit Feedback")}</SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {!isSupport && (
            <Tabs value={type} onValueChange={(v) => setType(v as FeedbackType)}>
              <TabsList className="w-full overflow-hidden">
                <TabsTrigger value="bug" className="flex-1 text-xs truncate">{fb?.typeBug ?? "Bug Report"}</TabsTrigger>
                <TabsTrigger value="feature" className="flex-1 text-xs truncate">{fb?.typeFeature ?? "Feature"}</TabsTrigger>
                <TabsTrigger value="support" className="flex-1 text-xs truncate">{fb?.supportTab ?? "Support"}</TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          <div className="space-y-2">
            <Label>{isSupport ? (fb?.supportSubjectLabel ?? "Subject") : (fb?.titleLabel ?? "Title")}</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={isSupport ? (fb?.supportSubjectPlaceholder ?? "What do you need help with?") : (fb?.titlePlaceholder ?? "Brief summary...")} maxLength={200} />
            {!isSupport && <DuplicateHints title={title} type={type} />}
          </div>

          <div className="space-y-2">
            <Label>{isSupport ? (fb?.supportMessageLabel ?? "Message") : (fb?.descriptionLabel ?? "Description")}</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder={isSupport ? (fb?.supportMessagePlaceholder ?? "Describe your issue or question...") : (fb?.descriptionPlaceholder ?? "Describe in detail...")} rows={4} />
          </div>

          {/* Contact info for support */}
          {isSupport && (
            <div className="space-y-2">
              {user ? (
                <>
                  <Label>{fb?.loggedInAsLabel ?? "Logged in as"}</Label>
                  <div className="flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-sm text-secondary-foreground">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{profile?.email ?? user.email}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{fb?.loggedInHint ?? "We'll reply to your account email."}</p>
                </>
              ) : (
                <>
                  <Label>{fb?.contactEmailLabel ?? "Your Email"}</Label>
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder={fb?.contactEmailPlaceholder ?? "your@email.com"}
                  />
                  <p className="text-xs text-muted-foreground">{fb?.contactEmailHint ?? "So we can reply to your request."}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      onOpenChange(false);
                      navigate("/login");
                    }}
                  >
                    <LogIn className="h-4 w-4" />
                    {fb?.supportLoginInstead ?? "Or log in instead"}
                  </Button>
                </>
              )}
            </div>
          )}

          {type === "bug" && (
            <>
              <div className="space-y-2">
                <Label>{fb?.stepsLabel ?? "Steps to Reproduce"}</Label>
                <Textarea value={steps} onChange={(e) => setSteps(e.target.value)} placeholder={fb?.stepsPlaceholder ?? "1. Go to...\n2. Click on..."} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>{fb?.expectedLabel ?? "Expected Behavior"}</Label>
                <Input value={expected} onChange={(e) => setExpected(e.target.value)} placeholder={fb?.expectedPlaceholder ?? "What should happen?"} />
              </div>
              <div className="space-y-2">
                <Label>{fb?.actualLabel ?? "Actual Behavior"}</Label>
                <Input value={actual} onChange={(e) => setActual(e.target.value)} placeholder={fb?.actualPlaceholder ?? "What actually happens?"} />
              </div>

              <div className="space-y-2">
                <Label>{fb?.screenshots ?? "Screenshots"} ({files.length}/3)</Label>
                <div className="flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs text-secondary-foreground">
                      {f.name.slice(0, 20)}
                      <button onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {files.length < 3 && (
                    <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 rounded-md border border-dashed border-border px-3 py-1 text-xs text-muted-foreground hover:border-primary hover:text-primary">
                      <Upload className="h-3 w-3" /> {fb?.addScreenshot ?? "Add"}
                    </button>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </div>
            </>
          )}

          {/* Email notification opt-in (only for logged-in, non-support) */}
          {user && !isSupport && (
            <div className="flex items-center gap-2">
              <Checkbox
                id="notify-email"
                checked={notifyEmail}
                onCheckedChange={(c) => setNotifyEmail(!!c)}
              />
              <label htmlFor="notify-email" className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
                <BellRing className="h-3.5 w-3.5" />
                {fb?.subscribeEmail ?? "Notify me by email"}
              </label>
            </div>
          )}

          <Button onClick={handleSubmit} disabled={submitting || !title.trim() || !description.trim()} className="w-full">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {isSupport ? (fb?.submitSupport ?? "Send Request") : (fb?.submit ?? "Submit")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
